import { Formik } from "formik";
import { NextPage } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import * as yup from "yup";

import { AppPath } from "../appPath";
import { AuthFooter } from "../auth/components/AuthFooter";
import { AuthLayout } from "../auth/components/AuthLayout";
import { EmailConfirmation } from "../auth/components/EmailConfirmation";
import { ExternalSignIn } from "../auth/components/ExternalSignIn";
import { GeneralFormError } from "../auth/components/GeneralFormError";
import { useSignIn } from "../auth/hooks/useSignIn";
import { withPublicOnly } from "../auth/withPublicOnly";
import { BusyBtn } from "../main/components/buttons/BusyBtn";
import { HrWithText } from "../main/components/HrWithText";
import { Layout } from "../main/components/Layout";
import { NvCaptcha } from "../main/components/NvCaptcha";
import { boolProp } from "../main/utils/boolProp";
import { EMAIL_MAX_LENGTH, USERNAME_MIN_LENGTH } from "../main/utils/globalConst";

interface FormData {
    id: string;
    password: string;
}

const validationSchema = yup.object<Record<keyof FormData, yup.BaseSchema>>({
    id: yup.string().required().min(USERNAME_MIN_LENGTH),
    password: yup.string().required(),
});

const SignInPage: NextPage = () => {
    const [isDisabled, setIsDisabled] = useState(false);
    const captchaRef = useRef<NvCaptcha>();
    const [password, setPassword] = useState("");
    const { notConfirmedEmail, isProcessing, errorMsg, clearError, login } = useSignIn();
    const router = useRouter();

    async function handleSubmit(data: FormData) {
        setPassword(data.password);
        const reCaptchaToken = await captchaRef.current.execute();
        await login(data.id, data.password, reCaptchaToken);
    }

    async function handleEmailConfirmed() {
        const reCaptchaToken = await captchaRef.current.execute();
        const success = await login(notConfirmedEmail, password, reCaptchaToken);
        if (!success) {
            router.replace("/");
        }
    }

    return (
        <Layout title="Sign in" description="Sign in to Nostwave with your login, Google or Facebook account." noNavigation fullPage>
            <NvCaptcha ref={captchaRef} />

            {notConfirmedEmail && <EmailConfirmation email={notConfirmedEmail} onConfirmed={handleEmailConfirmed} resendEnabled />}

            {!notConfirmedEmail && (
                <AuthLayout header="Sign in with username or email" footer={<AuthFooter page="signIn" />}>
                    <Formik<FormData>
                        validationSchema={validationSchema}
                        onSubmit={(values) => handleSubmit(values)}
                        initialValues={{
                            id: "",
                            password: "",
                        }}
                    >
                        {({ handleSubmit, handleChange, touched, errors, setFieldTouched }) => (
                            <Form noValidate onSubmit={handleSubmit}>
                                <fieldset disabled={isProcessing || isDisabled}>
                                    <div className="d-grid gap-3">
                                        <Form.Group controlId="id">
                                            <Form.Control
                                                type="text"
                                                autoComplete="username"
                                                inputMode="email"
                                                placeholder="Username or email address"
                                                onChange={handleChange}
                                                onBlur={() => setFieldTouched("id")}
                                                isInvalid={touched.id && !!errors.id}
                                                maxLength={EMAIL_MAX_LENGTH}
                                            />
                                            <Form.Control.Feedback type="invalid">Username or email is required.</Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group controlId="password">
                                            <Form.Control
                                                type="password"
                                                autoComplete="current-password"
                                                placeholder="Password"
                                                onChange={handleChange}
                                                onBlur={() => setFieldTouched("password")}
                                                isInvalid={touched.password && !!errors.password}
                                            />
                                            <Form.Control.Feedback type="invalid">Password is required.</Form.Control.Feedback>
                                        </Form.Group>

                                        <div className="d-grid">
                                            <BusyBtn busy={boolProp(isProcessing)} variant="primary" type="submit">
                                                Sign in
                                            </BusyBtn>
                                        </div>
                                    </div>
                                </fieldset>

                                <GeneralFormError className="mt-1" msg={errorMsg} onClick={clearError} />
                            </Form>
                        )}
                    </Formik>

                    <Link href={AppPath.forgotPassword} passHref prefetch={false}>
                        <a className="small">I forgot my password...</a>
                    </Link>

                    <HrWithText text="or"></HrWithText>

                    <ExternalSignIn disabled={isProcessing} busyChanged={setIsDisabled} className="mt-2" mode="signIn" />
                </AuthLayout>
            )}
        </Layout>
    );
};

export default withPublicOnly(SignInPage);
